import { useNavigate } from 'react-router-dom';
import { useState, useEffect } from 'react';

export default function StateTaxCompliancePage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-xl border-b border-gray-200 shadow-sm' : 'bg-white/80 backdrop-blur-md'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <a href="/" className="flex items-center hover:opacity-90 transition-opacity">
              <img 
                alt="InstaVisible Tax & Savings Solutions Logo" 
                className="h-14 w-auto object-contain" 
                src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
              />
            </a>
            <button onClick={() => navigate('/blog')} className="inline-flex items-center cursor-pointer gap-2 px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors rounded-lg hover:bg-gray-100 whitespace-nowrap">
              <i className="ri-arrow-left-line"></i>
              Back to Blog
            </button>
          </div>
        </div>
      </nav>

      {/* Article Header */}
      <article className="pt-32 pb-16">
        <header className="px-6 lg:px-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-6">
              <span className="px-3 py-1 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full">State Tax</span>
              <span className="text-gray-500">•</span>
              <time className="text-gray-600">January 5, 2025</time>
              <span className="text-gray-500">•</span>
              <span className="text-gray-600">12 min read</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Multi-State Tax Compliance: Navigating 50 Different Tax Systems
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Simplify multi-state tax filing with AI-powered projections. Learn strategies for managing state-specific requirements and avoiding costly compliance mistakes.
            </p>
          </div>
        </header>

        {/* Featured Image */}
        <div className="px-6 lg:px-8 mb-12">
          <div className="max-w-5xl mx-auto">
            <div className="relative w-full h-96 rounded-2xl overflow-hidden">
              <img 
                src="https://public.readdy.ai/ai/img_res/7ae1cb47922fcfa92494b98f39802c76.jpg"
                alt="Multi-State Tax Compliance"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div className="px-6 lg:px-8">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">The Multi-State Tax Challenge</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                In an increasingly mobile and digital economy, businesses and individuals frequently operate across state lines. Remote work, multi-state business operations, investment properties in different states, and digital commerce have made multi-state tax compliance one of the most complex challenges facing tax professionals today.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Unlike the federal tax system with its unified rules and forms, each of the 50 states (plus the District of Columbia) operates its own tax system with unique rates, rules, forms, filing deadlines, and compliance requirements. What's deductible in one state may not be in another. Residency rules vary dramatically. Apportionment formulas differ. The complexity multiplies exponentially with each additional state involved.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Understanding State Tax Nexus</h2>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">What Creates Tax Nexus?</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Nexus is the connection between a taxpayer and a state that creates a tax obligation. Historically, physical presence was required—an office, employees, or property in the state. However, the 2018 Supreme Court decision in South Dakota v. Wayfair fundamentally changed this landscape, establishing that economic presence alone can create nexus.
              </p>

              <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Common Nexus Triggers</h4>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700"><strong>Physical presence:</strong> Offices, warehouses, employees, or property in the state</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700"><strong>Economic nexus:</strong> Exceeding state-specific revenue or transaction thresholds</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700"><strong>Click-through nexus:</strong> Affiliate relationships generating sales in the state</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700"><strong>Marketplace nexus:</strong> Sales through third-party platforms like Amazon or eBay</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <i className="ri-check-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                    <span className="text-gray-700"><strong>Remote employees:</strong> Having employees working remotely from other states</span>
                  </li>
                </ul>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Individual vs. Business Nexus</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                For individuals, nexus typically relates to residency and domicile. Most states tax residents on all income regardless of source, while nonresidents are taxed only on income sourced to that state. Determining residency involves factors like physical presence (often 183 days), voter registration, driver's license, and intent to remain.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                For businesses, nexus determines where the business must file returns and pay taxes. A business can have nexus in multiple states simultaneously, requiring careful tracking of activities, revenue, and presence in each jurisdiction.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">State Income Tax Variations</h2>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">The Spectrum of State Tax Systems</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                State income tax systems vary dramatically across the country:
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">No Income Tax States</h4>
                  <p className="text-gray-700 text-sm mb-3">Nine states impose no individual income tax:</p>
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">Alaska</span>
                    <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">Florida</span>
                    <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">Nevada</span>
                    <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">South Dakota</span>
                    <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">Tennessee</span>
                    <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">Texas</span>
                    <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">Washington</span>
                    <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">Wyoming</span>
                    <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded-full text-xs font-medium">New Hampshire</span>
                  </div>
                </div>

                <div className="bg-emerald-50 border-2 border-emerald-200 rounded-xl p-6">
                  <h4 className="text-lg font-bold text-gray-900 mb-3">Flat Tax States</h4>
                  <p className="text-gray-700 text-sm mb-3">Several states use a single flat rate:</p>
                  <ul className="space-y-2 text-sm">
                    <li className="flex items-center gap-2">
                      <i className="ri-arrow-right-s-line text-emerald-600"></i>
                      <span className="text-gray-700">Colorado (4.40%)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-arrow-right-s-line text-emerald-600"></i>
                      <span className="text-gray-700">Illinois (4.95%)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-arrow-right-s-line text-emerald-600"></i>
                      <span className="text-gray-700">Indiana (3.15%)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-arrow-right-s-line text-emerald-600"></i>
                      <span className="text-gray-700">Michigan (4.25%)</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <i className="ri-arrow-right-s-line text-emerald-600"></i>
                      <span className="text-gray-700">North Carolina (4.75%)</span>
                    </li>
                  </ul>
                </div>
              </div>

              <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6 mb-6">
                <h4 className="text-lg font-bold text-gray-900 mb-3">Progressive Tax States</h4>
                <p className="text-gray-700 text-sm mb-3">
                  Most states use graduated tax brackets with rates increasing as income rises. California has the highest top rate at 13.3%, while some states have top rates below 5%. Each state's bracket structure, standard deductions, and credits create unique effective tax rates.
                </p>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Key State Tax Differences</h3>
              <ul className="space-y-4 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Federal conformity:</strong> Some states start with federal AGI, others with federal taxable income, and some use their own calculation entirely</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Standard deductions:</strong> Amounts vary significantly; some states don't offer standard deductions at all</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Itemized deductions:</strong> States may limit or disallow certain federal itemized deductions</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Tax credits:</strong> Each state offers unique credits for education, child care, energy efficiency, and other purposes</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-alert-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Treatment of retirement income:</strong> Some states fully exempt Social Security and pension income; others tax it fully</span>
                </li>
              </ul>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Multi-State Business Apportionment</h2>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Allocating Business Income Across States</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                When a business operates in multiple states, it must apportion its income among those states for tax purposes. This prevents the same income from being taxed multiple times while ensuring each state receives its fair share of tax revenue.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">The Three-Factor Formula (Traditional)</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Historically, most states used a three-factor apportionment formula based on:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Property:</strong> The ratio of property in the state to total property</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Payroll:</strong> The ratio of payroll in the state to total payroll</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-fill text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Sales:</strong> The ratio of sales in the state to total sales</span>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Modern Apportionment Trends</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Many states have moved away from the traditional three-factor formula, instead using:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Single sales factor:</strong> Apportionment based solely on sales, encouraging businesses to locate operations in the state</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Double-weighted sales:</strong> Sales factor counted twice in the formula</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-arrow-right-s-line text-emerald-600 text-xl mt-1 flex-shrink-0"></i>
                  <span className="text-gray-700"><strong>Market-based sourcing:</strong> Sales assigned based on where customers are located rather than where services are performed</span>
                </li>
              </ul>

              <div className="bg-teal-50 border-2 border-teal-200 rounded-xl p-6 mb-6">
                <h4 className="text-xl font-bold text-gray-900 mb-4">Apportionment Complexity Example</h4>
                <p className="text-gray-700 text-sm leading-relaxed">
                  A software company headquartered in California with remote employees in Texas and customers nationwide must navigate California's single sales factor formula, determine where sales are sourced under market-based rules, track employee locations for potential nexus creation, and ensure compliance with economic nexus thresholds in states where customers are located—all while maintaining detailed records to support apportionment calculations.
                </p>
              </div>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Common Multi-State Tax Pitfalls</h2>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">1. Failing to Identify Nexus</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Many businesses don't realize they've created nexus in a state until they receive a notice from that state's tax authority. By then, they may owe back taxes, penalties, and interest. Regular nexus reviews are essential, especially as business activities change.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">2. Incorrect Residency Determinations</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Individuals who split time between states often face residency audits. States aggressively pursue high-income individuals claiming nonresident status. Maintaining detailed records of time spent in each state, along with documentation of domicile factors, is crucial.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">3. Missing State-Specific Credits and Deductions</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Each state offers unique tax benefits that can significantly reduce tax liability. Missing these opportunities means overpaying taxes. Conversely, claiming credits or deductions not available in a particular state can trigger audits.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">4. Apportionment Errors</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Incorrect apportionment calculations can result in significant over- or under-payment of state taxes. With different states using different formulas and sourcing rules, maintaining accurate records and applying the correct methodology for each state is challenging.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">5. Missing Filing Deadlines</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                State filing deadlines vary and don't always align with the federal April 15 deadline. Some states have different deadlines for different entity types. Missing deadlines results in penalties and interest that can be substantial.
              </p>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Strategies for Multi-State Tax Compliance</h2>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Implement Robust Tracking Systems</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Maintain detailed records of business activities in each state, including sales, property, payroll, and employee locations. For individuals, track days spent in each state and maintain documentation of domicile factors.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Conduct Regular Nexus Studies</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Review business activities at least annually to identify new nexus creation. Consider both physical and economic presence. Evaluate whether voluntary disclosure programs might be beneficial for past non-compliance.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Leverage Technology and Automation</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Manual multi-state tax compliance is error-prone and time-consuming. AI-powered tax software can automatically track nexus, calculate apportionment, apply state-specific rules, and generate accurate returns for all required states.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Stay Current on State Tax Law Changes</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                State tax laws change frequently. Subscribe to state tax updates, participate in continuing education, and leverage technology that automatically incorporates the latest state tax law changes.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Consider Tax Planning Opportunities</h3>
              <p className="text-gray-700 leading-relaxed mb-6">
                Multi-state operations create planning opportunities. Strategic location of operations, intellectual property holding companies, and careful structuring of transactions can significantly reduce overall state tax burden while maintaining full compliance.
              </p>
            </section>

            <section className="mb-12 bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-6">Simplify Multi-State Tax Compliance</h2>
              <p className="text-white/90 leading-relaxed mb-6">
                InstaVisible's AI-powered platform automatically handles multi-state tax compliance across all 50 states. Our intelligent system tracks nexus, calculates apportionment, applies state-specific rules, and generates accurate returns—reducing compliance time by up to 80% while eliminating costly errors. Focus on strategic tax planning while we handle the complexity.
              </p>
              <button onClick={() => navigate('/')} className="inline-flex items-center cursor-pointer justify-center gap-2 whitespace-nowrap bg-white text-emerald-700 hover:bg-gray-50 text-base font-semibold px-8 h-14 rounded-xl shadow-lg transition-all duration-300 hover:scale-105">
                Request a Video Demo
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </section>

            <section className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Conclusion</h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Multi-state tax compliance represents one of the most complex challenges in modern tax practice. With 50 different tax systems, varying nexus rules, diverse apportionment formulas, and constantly changing regulations, managing multi-state obligations manually is increasingly impractical.
              </p>
              <p className="text-gray-700 leading-relaxed">
                The future of multi-state tax compliance lies in intelligent automation that can track activities across jurisdictions, apply state-specific rules accurately, and generate compliant returns efficiently. By embracing AI-powered solutions, tax professionals can transform multi-state compliance from a time-consuming burden into a streamlined process, allowing them to focus on strategic planning and client service.
              </p>
            </section>
          </div>
        </div>

        {/* Related Articles */}
        <div className="px-6 lg:px-8 mt-16">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">Related Articles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <a href="/blog/understanding-form-1040" className="group p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-emerald-400 transition-all">
                <span className="text-sm text-emerald-600 font-semibold mb-2 block">Tax Forms</span>
                <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">Understanding Form 1040: A Complete Guide for Tax Professionals</h4>
                <p className="text-gray-600 text-sm">Master the fundamentals of Form 1040 and its schedules.</p>
              </a>
              <a href="/blog/business-tax-forms-guide" className="group p-6 bg-white border-2 border-gray-200 rounded-xl hover:border-emerald-400 transition-all">
                <span className="text-sm text-emerald-600 font-semibold mb-2 block">Business Tax</span>
                <h4 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-emerald-600 transition-colors">Business Tax Forms Explained: 1065, 1120, and 1120-S</h4>
                <p className="text-gray-600 text-sm">Navigate the complexities of business tax returns.</p>
              </a>
            </div>
          </div>
        </div>
      </article>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-emerald-600 mt-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center mb-6">
                <img 
                  alt="InstaVisible Tax & Savings Solutions Logo" 
                  className="h-14 w-auto object-contain brightness-0 invert" 
                  src="https://static.readdy.ai/image/4a891bf01ca77aa59d4e684a3f5f885b/e200ded0512fa4f1c42cf2a76268a351.webp"
                />
              </div>
              <p className="text-white/90 leading-relaxed max-w-md">
                Professional AI-powered tax solutions for CPAs, tax professionals and CFOs. Streamline your workflow with cutting-edge technology.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Product</h3>
              <ul className="space-y-4">
                <li><a href="/" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Services</a></li>
                <li><a href="/ai-agents" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">AI Agents</a></li>
                <li><a href="/blog" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Blog</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold text-white mb-6">Company</h3>
              <ul className="space-y-4">
                <li><a href="/" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Contact</a></li>
                <li><a href="/privacy-policy" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Privacy Policy</a></li>
                <li><a href="/terms-of-use" className="text-white/80 hover:text-white transition-colors whitespace-nowrap">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          
          <div className="pt-8 border-t border-white/20 flex flex-col sm:flex-row items-center justify-between gap-6">
            <p className="text-white/80">© 2025 InstaVisible Tax & Savings Solutions. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
